import React from 'react';
import Section from '../Section/Section';
import Typography from '../Typography/Typography';
import Spacer from '../Spacer/Spacer';
import { useModal } from '../Modal/useModal';
import { Button } from '../Button/Button';
import { Stack } from '../Stack/Stack';
import { useTranslation, Trans } from 'utils/translation';
import { useAppSettings } from 'modules/ui/hooks';

const templates = {
  nl: {
    to: 'admin@quodoc.com',
    subject: 'Aanvraag demo account Quodoc Light',
    body: `Graag wil ik vrijblijvend een demo-versie laten activeren van Quodoc Light.
Mijn gegevens:

Naam:
Voornaam:
Tel.:`,
  },
  fr: {
    to: 'admin@quodoc.com',
    subject: 'Demande de compte de démonstration Quodoc Light',
    body: `Je souhaite activer une version de démonstration de Quodoc Light sans engagement.
Mes coordonnées :

Nom :
Prénom :
Tél. :`,
  },
  en: {
    to: 'admin@quodoc.com',
    subject: 'Request demo account Quodoc Light',
    body: `I would like to request a free demo version of Quodoc Light.

My contact details:

Name:
First name:
Phone:`,
  },
} as const;

function createMailtoLink({
  to,
  subject,
  body,
}: {
  to: string;
  subject: string;
  body: string;
}) {
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const DemoRequestModal = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useAppSettings();

  function createMailLinkInCurrentLanguage() {
    if (Object.keys(templates).includes(i18n.language)) {
      return createMailtoLink(
        templates[i18n.language as keyof typeof templates],
      );
    }

    return createMailtoLink(templates.nl);
  }

  return (
    <Section>
      <Section>
        <Spacer size={4} />
        <Typography>
          <strong style={{ fontSize: '20px' }}>
            {t('demo_request_intro')}
          </strong>
        </Typography>
        <Spacer size={4} />
        <Typography>{t('demo_request_conversion')}</Typography>
        <Spacer size={4} />
        <Typography>
          <strong>
            {t('demo_request_pricing', { client: settings.clientName })}
          </strong>
        </Typography>
        <Spacer size={4} />
        <Typography>
          <Trans
            i18nKey="demo_request_upgrade_info"
            values={{
              link: t('demo_request_all_features'),
            }}
            components={{
              a: (
                <a
                  className="link"
                  target="_blank"
                  href="https://www.quodoc.com/functies"
                />
              ),
            }}
          />
        </Typography>
        <Spacer size={4} />
        <Typography>{t('demo_request_support')}</Typography>
        <Spacer size={4} />
        <Stack direction="row" justify="space-between" align="center">
          <Button
            onClick={() =>
              (window.location.href = createMailLinkInCurrentLanguage())
            }
          >
            {t('demo_request_button')}
          </Button>
          <a className="link" target="_blank" href="https://quodoc.com">
            <strong>{t('demo_request_website')}</strong>
          </a>
        </Stack>
      </Section>
    </Section>
  );
};

export const useDemoRequestModal = () => {
  const modal = useModal();
  const { t } = useTranslation();

  return () =>
    modal.open(DemoRequestModal, {
      title: t('demo_request_title'),
      size: 'full',
    });
};

export default DemoRequestModal;
